import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Typography } from "@mui/material";
import { format } from "date-fns";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useRetrieveUserIntraSquadTournamentPermissions } from "../api/PermissionApiProvider";
import { useRetrieveIntraSquadTournamentMatch, useSubmitIntraSquadTournamentMatchStats, } from "../api/TournamentApiProvider";
import { useRetrieveTeam, useRetrieveUserTeamPermissions, } from "../ApiProvider";
import { ErrorView } from "../nav/Error";
import { Forbidden } from "../nav/Forbidden";
import { Loading } from "../nav/Loading";
import { IntraSquadTournamentMatchStatsForm } from "../stats/IntraSquadTournamentMatchStatsForm";
import { toMatchPlayerStat } from "../stats/utils";
import ErrorSnackbar from "../utils/ErrorSnackbar";
import { useNumericParams } from "../utils/useNumericParams";
export const UpdatePlayerIntraSquadTournamentMatchStats = () => {
    const { id } = useNumericParams();
    const { isLoading: useRetrieveIntraSquadTournamentMatchIsLoading, error: useRetrieveIntraSquadTournamentMatchError, data: intraSquadTournamentMatch, } = useRetrieveIntraSquadTournamentMatch({ id });
    const { data: userTournamentPermissions, isLoading: userTournamentPermissionsIsLoading, error: userTournamentPermissionsError, } = useRetrieveUserIntraSquadTournamentPermissions({
        tournamentId: intraSquadTournamentMatch?.tournament,
    });
    const { data: userTeamPermissions, isLoading: userTeamPermissionsIsLoading, error: userTeamPermissionsError, } = useRetrieveUserTeamPermissions({
        teamId: intraSquadTournamentMatch?.team,
    });
    const { canModifyStats: canModifyTournamentStats = false } = userTournamentPermissions || {};
    const { canModifyStats: canModifyTeamStats = false } = userTeamPermissions || {};
    // Stats entry needs stats-write permission (manager or assistant manager),
    // not full team-modify rights.
    const canModify = canModifyTournamentStats || canModifyTeamStats;
    const navigate = useNavigate();
    const { isLoading: submitStatsIsLoading, isError: submitStatsIsError, mutate: submitStats, } = useSubmitIntraSquadTournamentMatchStats(intraSquadTournamentMatch);
    const { data: team, isLoading: teamIsLoading, error: teamError, } = useRetrieveTeam({
        id: intraSquadTournamentMatch?.team,
        enabled: canModify,
    });
    // Check permissions loading states first, before any data loading
    if (userTournamentPermissionsIsLoading || userTeamPermissionsIsLoading) {
        return _jsx(Loading, {});
    }
    // Check permissions error states first
    if (userTournamentPermissionsError || userTeamPermissionsError) {
        return (_jsx(ErrorView, { error: userTournamentPermissionsError || userTeamPermissionsError }));
    }
    // Check tournament match loading/error after permissions
    if (useRetrieveIntraSquadTournamentMatchIsLoading) {
        return _jsx(Loading, {});
    }
    if (useRetrieveIntraSquadTournamentMatchError) {
        return _jsx(ErrorView, { error: useRetrieveIntraSquadTournamentMatchError });
    }
    // Check permissions before proceeding with any other data fetching
    if (!canModify) {
        return _jsx(Forbidden, {});
    }
    // Now check secondary data loading states (only if permissions allow)
    if (teamIsLoading) {
        return _jsx(Loading, {});
    }
    // Check secondary data error states (only if permissions allow)
    if (teamError) {
        return _jsx(ErrorView, { error: teamError });
    }
    const onSubmit = (intraSquadTournamentMatchResult, playerIntraSquadTournamentMatchStats) => {
        submitStats({
            result: {
                teamAScore: intraSquadTournamentMatchResult.teamAScore,
                teamBScore: intraSquadTournamentMatchResult.teamBScore,
                matchReport: intraSquadTournamentMatchResult.matchReport,
            },
            playerStats: Object.values(playerIntraSquadTournamentMatchStats).map(toMatchPlayerStat),
        }, {
            onSuccess: () => navigate(`/teams/${intraSquadTournamentMatch.team}/intra-squad-tournaments/${intraSquadTournamentMatch.tournament}/`),
        });
    };
    const error = useRetrieveIntraSquadTournamentMatchError || teamError;
    if (error) {
        return _jsx(ErrorView, { error: error });
    }
    if (!team || !intraSquadTournamentMatch) {
        return (_jsx(ErrorView, { error: new Error("Failed to load user data correctly.") }));
    }
    return (_jsxs(React.Fragment, { children: [_jsx(ErrorSnackbar, { open: submitStatsIsError, message: "Failed to save tournament match stats! Please try again." }), _jsx(Typography, { variant: "h6", children: _jsxs("strong", { children: ["Intra-Squad Match Stats for Team ", team.name] }) }), _jsx(Typography, { variant: "subtitle2", children: format(new Date(intraSquadTournamentMatch.kickOff), "MMMM do yyyy @ H:mma") }), _jsx(IntraSquadTournamentMatchStatsForm, { onSubmit: onSubmit, intraSquadTournamentMatch: intraSquadTournamentMatch, isSubmitting: submitStatsIsLoading })] }));
};
